/**
 * i18n — Simple dictionary-based translations for FR (default) and EN.
 *
 * Wizard questions/options come from the backend already translated
 * (they include {label: {fr, en}} pairs). The UI chrome is here.
 */

export const LANGS = ["fr", "en"];
export const DEFAULT_LANG = "fr";
const KEY = "ariovis.lang";

export function getStoredLang() {
  try {
    const v = localStorage.getItem(KEY);
    if (v && LANGS.includes(v)) return v;
  } catch (_) { /* ignore */ }
  try {
    const nav = (navigator.language || "fr").slice(0, 2).toLowerCase();
    if (LANGS.includes(nav)) return nav;
  } catch (_) { /* ignore */ }
  return DEFAULT_LANG;
}

export function setStoredLang(lang) {
  try { localStorage.setItem(KEY, lang); } catch (_) { /* ignore */ }
}

/* ------------------------------------------------------------------ */
/* Dictionary                                                         */
/* ------------------------------------------------------------------ */
export const dict = {
  // Header
  "nav.method": { fr: "Méthode", en: "Method" },
  "nav.deliverable": { fr: "Ce que vous recevrez", en: "What you get" },
  "nav.faq": { fr: "FAQ", en: "FAQ" },
  "nav.discover": { fr: "Découvrir Ariovis", en: "Discover Ariovis" },
  "nav.start_cta": { fr: "Générer ma roadmap", en: "Generate my roadmap" },
  "nav.back_home": { fr: "Accueil", en: "Home" },
  "lang.switch_to": { fr: "EN", en: "FR" },
  "lang.aria": { fr: "Passer en anglais", en: "Switch to French" },

  // Landing — Hero
  "hero.badge": { fr: "ÉTUDE GRATUITE", en: "FREE STUDY" },
  "hero.title": {
    fr: "Jusqu'où votre IAM est-il réellement aligné avec le Zero Trust ?",
    en: "How truly aligned is your IAM with Zero Trust?",
  },
  "hero.subtitle": {
    fr: "Décrivez votre organisation, vos usages et vos technologies. Obtenez votre niveau de maturité, vos principaux écarts et une roadmap personnalisée en cinq étapes.",
    en: "Describe your organization, your uses and your technologies. Get your maturity level, key gaps and a personalized 5-step roadmap.",
  },
  "hero.trust.time": { fr: "≈ 8 minutes", en: "≈ 8 minutes" },
  "hero.trust.confidential": { fr: "Aucune donnée technique sensible demandée", en: "No sensitive technical data required" },
  "hero.trust.pdf": { fr: "Rapport PDF personnalisé", en: "Personalized PDF report" },
  "hero.trust.no_commit": { fr: "Sans engagement", en: "No commitment" },
  "hero.cta_primary": { fr: "Commencer mon diagnostic", en: "Start my assessment" },
  "hero.cta_secondary": { fr: "Voir la méthode", en: "See the method" },
  "hero.resume_hint": { fr: "Vous pourrez revenir sur vos réponses avant de générer l'étude.", en: "You can review your answers before generating the study." },

  "problem.overline": { fr: "CONSTAT", en: "OBSERVATION" },
  // Landing — Problem
  "problem.title": {
    fr: "Une accumulation d'outils ne constitue pas une architecture Zero Trust",
    en: "A stack of tools does not make a Zero Trust architecture",
  },
  "problem.p1": { fr: "Les identités ne sont pas toujours gouvernées de bout en bout.", en: "Identities are not always governed end-to-end." },
  "problem.p2": { fr: "Les applications utilisent des modèles d'accès hétérogènes.", en: "Applications rely on heterogeneous access models." },
  "problem.p3": { fr: "Les signaux de risque restent cloisonnés.", en: "Risk signals stay siloed." },
  "problem.p4": { fr: "Les contrôles réseau, privilégiés et applicatifs ne prennent pas toujours des décisions cohérentes.", en: "Network, privileged and application controls don't always agree." },
  "problem.conclusion": {
    fr: "La première étape consiste à comprendre les capacités réellement disponibles, leur couverture et leurs dépendances.",
    en: "The first step is to understand what capabilities you actually have, their coverage and their dependencies.",
  },

  // Landing — Méthode
  "method.overline": { fr: "MÉTHODE", en: "METHOD" },
  "method.title": { fr: "Cinq paliers, une trajectoire lisible", en: "Five tiers, one readable trajectory" },
  "method.subtitle": {
    fr: "Votre cible dépend de vos risques, de vos applications, de vos populations et de votre capacité à opérer les contrôles dans la durée.",
    en: "Your target depends on your risks, your applications, your populations and your ability to operate controls over time.",
  },
  "method.level.1.title": { fr: "Socle IAG + AM", en: "IAG + AM foundation" },
  "method.level.1.short": { fr: "Fondation", en: "Foundation" },
  "method.level.1.desc": { fr: "Identités fiables, cycle de vie maîtrisé, authentification centralisée.", en: "Reliable identities, controlled lifecycle, centralized authentication." },
  "method.level.2.title": { fr: "Réaction d'urgence", en: "Emergency reaction" },
  "method.level.2.short": { fr: "Réaction", en: "Reaction" },
  "method.level.2.desc": { fr: "Suspendre une identité, révoquer une session, alimenter le SOC.", en: "Suspend an identity, revoke a session, feed the SOC." },
  "method.level.3.title": { fr: "Autorisation contextuelle", en: "Contextual authorization" },
  "method.level.3.short": { fr: "Contextualisation", en: "Contextualization" },
  "method.level.3.desc": { fr: "Décisions fines : attributs, ressources, risque, terminal.", en: "Fine-grained decisions: attributes, resources, risk, device." },
  "method.level.4.title": { fr: "Extension aux accès", en: "Coverage of accesses" },
  "method.level.4.short": { fr: "Extension", en: "Extension" },
  "method.level.4.desc": { fr: "PAM, API et NHI, ZTNA — tous les points d'application.", en: "PAM, APIs and NHIs, ZTNA — every enforcement point." },
  "method.level.5.title": { fr: "Politiques dynamiques", en: "Dynamic policies" },
  "method.level.5.short": { fr: "Orchestration", en: "Orchestration" },
  "method.level.5.desc": { fr: "Décisions externalisées, en temps réel, gouvernées et résilientes.", en: "Externalized decisions, real-time, governed and resilient." },

  // Landing — Livrable
  "deliverable.overline": { fr: "CE QUE VOUS RECEVREZ", en: "WHAT YOU RECEIVE" },
  "deliverable.title": { fr: "Un rapport personnalisé, prêt à partager", en: "A personalized report, ready to share" },
  "deliverable.item.level": { fr: "Votre niveau actuel et votre cible recommandée", en: "Your current level and recommended target" },
  "deliverable.item.radar": { fr: "Un radar par domaine (10 axes)", en: "A radar chart across 10 domains" },
  "deliverable.item.forces": { fr: "Vos forces identifiées", en: "Your identified strengths" },
  "deliverable.item.gaps": { fr: "Vos écarts structurants et dépendances", en: "Structural gaps and dependencies" },
  "deliverable.item.wins": { fr: "Vos quick wins", en: "Your quick wins" },
  "deliverable.item.roadmap": { fr: "Une roadmap en cinq étapes", en: "A five-step roadmap" },
  "deliverable.item.matrix": { fr: "Une matrice des chantiers", en: "A workstreams priority matrix" },
  "deliverable.item.offer": { fr: "Une proposition d'accompagnement adaptée", en: "A tailored engagement proposal" },
  "deliverable.item.pdf": { fr: "Un PDF partageable en interne", en: "A PDF you can share internally" },

  // Landing — How it works
  "how.overline": { fr: "COMMENT ÇA MARCHE", en: "HOW IT WORKS" },
  "how.title": { fr: "Trois étapes. Une trajectoire.", en: "Three steps. One trajectory." },
  "how.step1.title": { fr: "Décrivez votre contexte", en: "Describe your context" },
  "how.step1.desc": { fr: "Secteur, populations, priorités, irritants.", en: "Sector, populations, priorities, pain points." },
  "how.step2.title": { fr: "Cartographiez vos capacités", en: "Map your capabilities" },
  "how.step2.desc": { fr: "IAG, AM, PAM, ZTNA, SOC, autorisation, API/NHI.", en: "IAG, AM, PAM, ZTNA, SOC, authorization, API/NHIs." },
  "how.step3.title": { fr: "Recevez votre trajectoire", en: "Receive your trajectory" },
  "how.step3.desc": { fr: "Niveau, cible, forces, écarts, roadmap, PDF.", en: "Level, target, strengths, gaps, roadmap, PDF." },

  // Landing — FAQ
  "faq.overline": { fr: "QUESTIONS FRÉQUENTES", en: "FREQUENTLY ASKED" },
  "faq.title": { fr: "Tout savoir avant de démarrer", en: "Everything to know before starting" },
  "faq.q1": { fr: "Qu'est-ce que le Zero Trust ?", en: "What is Zero Trust?" },
  "faq.a1": {
    fr: "Le Zero Trust est une approche d'architecture reposant sur la vérification continue, le principe du moindre privilège et la décision explicite d'accès. L'identité en constitue l'un des socles principaux.",
    en: "Zero Trust is an architecture approach relying on continuous verification, least privilege, and explicit access decisions. Identity is one of its main foundations.",
  },
  "faq.q2": { fr: "Faut-il déjà disposer d'une solution IAM ?", en: "Do I need an IAM solution already?" },
  "faq.a2": {
    fr: "Non. L'outil vous aide à évaluer où vous en êtes, quel que soit votre point de départ.",
    en: "No. The tool helps you assess where you stand, regardless of your starting point.",
  },
  "faq.q3": { fr: "Vais-je devoir fournir des informations confidentielles ?", en: "Do I have to provide confidential information?" },
  "faq.a3": {
    fr: "Non. Aucune configuration technique, aucun log, aucun secret. Uniquement des questions de haut niveau sur vos capacités et vos usages.",
    en: "No. No technical configuration, no logs, no secrets — only high-level questions about your capabilities and uses.",
  },
  "faq.q4": { fr: "Mon organisation doit-elle viser le niveau 5 ?", en: "Should my organization aim for level 5?" },
  "faq.a4": {
    fr: "Pas nécessairement. Le niveau cible dépend de vos risques, de vos applications, de vos populations et de votre capacité opérationnelle.",
    en: "Not necessarily. The target level depends on your risks, applications, populations, and operational capacity.",
  },
  "faq.q5": { fr: "Comment la maturité est-elle calculée ?", en: "How is maturity calculated?" },
  "faq.a5": {
    fr: "Chaque axe est évalué à partir de vos réponses (présence, couverture, gouvernance, intégration). Le niveau Ariovis dépend de prérequis explicites, pas d'une simple moyenne.",
    en: "Each axis is evaluated from your answers (presence, coverage, governance, integration). The Ariovis level depends on explicit prerequisites, not a simple average.",
  },
  "faq.q6": { fr: "Le rapport remplace-t-il un audit ?", en: "Does the report replace an audit?" },
  "faq.a6": {
    fr: "Non. Cette étude constitue une première orientation personnalisée. Elle ne remplace pas un audit approfondi.",
    en: "No. This study is a first personalized orientation. It does not replace an in-depth audit.",
  },
  "faq.q7": { fr: "Que se passe-t-il si une brique IAG, PAM, AM ou ZTNA manque ?", en: "What happens if an IAG, PAM, AM or ZTNA brick is missing?" },
  "faq.a7": {
    fr: "Le rapport le signale, explique la conséquence et propose une trajectoire adaptée (avec un renvoi ciblé lorsque pertinent).",
    en: "The report highlights it, explains the consequence, and proposes a tailored trajectory (with a targeted next step when relevant).",
  },
  "faq.q8": { fr: "Comment mes données sont-elles utilisées ?", en: "How is my data used?" },
  "faq.a8": {
    fr: "Elles servent à générer votre étude et à vous recontacter si vous y consentez. Aucune donnée n'est vendue.",
    en: "It is used to generate your study and contact you if you consent. No data is sold.",
  },
  "faq.q9": { fr: "Puis-je reprendre ou partager mon étude ?", en: "Can I resume or share my study?" },
  "faq.a9": {
    fr: "Oui. Vous recevez un lien signé permettant de reconsulter vos résultats.",
    en: "Yes. You receive a signed link that lets you view your results again.",
  },

  // Landing — Hero CTA subline
  "cta.final.title": { fr: "Prêt à objectiver votre trajectoire ?", en: "Ready to objectify your trajectory?" },
  "cta.final.subtitle": { fr: "8 minutes. Un PDF. Une décision documentée.", en: "8 minutes. One PDF. One documented decision." },

  // Wizard
  "results.five_year_plan": { fr: "Planning détaillé à 5 ans", en: "Detailed 5-year plan" },
  "results.five_year_intro": { fr: "Distribution concrète des chantiers sur 5 années, adaptée à votre cible.", en: "Concrete distribution of the workstreams over 5 years, adapted to your target." },
  "results.year_kpi": { fr: "Indicateur de réussite", en: "Success indicator" },
  "results.linked_step": { fr: "Étape de roadmap associée", en: "Linked roadmap step" },
  "results.scoping_title": { fr: "Cadrage détaillé — mapping en jours", en: "Detailed scoping — day-by-day mapping" },
  "results.scoping_col_task": { fr: "Sous-étape", en: "Sub-step" },
  "results.scoping_col_days": { fr: "Jours", en: "Days" },
  "results.scoping_col_price": { fr: "Montant (HT)", en: "Amount (excl. tax)" },
  "results.scoping_total": { fr: "Total (HT)", en: "Total (excl. tax)" },
  "results.approx_note": { fr: "Estimations approximatives, non contractuelles. TJM 800€, base 20 000€ / 25 j jusqu'à 12 000 utilisateurs, puis +2 000€ / +2,5 j par tranche de 5 000 utilisateurs.", en: "Approximate, non-binding estimates. Daily rate €800, baseline €20,000 / 25 days up to 12,000 users, then +€2,000 / +2.5 days per 5,000 additional users." },

  "wizard.step_of": { fr: "Étape {n} sur {total}", en: "Step {n} of {total}" },
  "wizard.previous": { fr: "Précédent", en: "Previous" },
  "wizard.next": { fr: "Suivant", en: "Next" },
  "wizard.finish": { fr: "Voir mes résultats", en: "See my results" },
  "wizard.dont_know": { fr: "Je ne sais pas", en: "I don't know" },
  "wizard.optional": { fr: "facultatif", en: "optional" },
  "wizard.required": { fr: "Ce champ est requis.", en: "This field is required." },
  "wizard.email_invalid": { fr: "Adresse email invalide.", en: "Invalid email address." },
  "wizard.consent_required": { fr: "Vous devez accepter pour recevoir votre étude.", en: "You must accept to receive your study." },
  "wizard.draft_restored": { fr: "Vos réponses en cours ont été restaurées.", en: "Your in-progress answers were restored." },
  "wizard.saving": { fr: "Génération en cours…", en: "Generating…" },
  "wizard.generic_error": { fr: "Une erreur est survenue. Merci de réessayer.", en: "Something went wrong. Please try again." },

  // Lead-gate teaser
  "teaser.title": { fr: "Votre diagnostic est prêt", en: "Your assessment is ready" },
  "teaser.text": {
    fr: "Indiquez vos coordonnées professionnelles pour recevoir votre roadmap complète et son PDF personnalisé.",
    en: "Enter your business contact details to receive your full roadmap and personalized PDF.",
  },
  "teaser.current_level": { fr: "Niveau actuel identifié", en: "Current level identified" },
  "teaser.priorities_count": { fr: "Chantiers prioritaires détectés", en: "Priority workstreams detected" },
  "teaser.target_hidden": { fr: "Niveau cible masqué", en: "Target level hidden" },
  "teaser.roadmap_hidden": { fr: "Roadmap floutée", en: "Roadmap blurred" },

  // Results
  "results.overline": { fr: "VOTRE ROADMAP", en: "YOUR ROADMAP" },
  "results.title": { fr: "Votre roadmap IAM Zero Trust", en: "Your IAM Zero Trust roadmap" },
  "results.subtitle": {
    fr: "Étude indicative personnalisée à partir de vos réponses.",
    en: "Indicative personalized study based on your answers.",
  },
  "results.download_pdf": { fr: "Télécharger mon rapport PDF", en: "Download my PDF report" },
  "results.share": { fr: "Copier le lien de reprise", en: "Copy resume link" },
  "results.share_copied": { fr: "Lien copié !", en: "Link copied!" },
  "results.summary": { fr: "Synthèse", en: "Summary" },
  "results.current_level": { fr: "Niveau actuel", en: "Current level" },
  "results.target_level": { fr: "Niveau cible recommandé", en: "Recommended target" },
  "results.global_score": { fr: "Score global indicatif", en: "Indicative global score" },
  "results.strongest_axis": { fr: "Axe le plus mature", en: "Most mature axis" },
  "results.weakest_axis": { fr: "Axe le plus fragile", en: "Weakest axis" },
  "results.axis_radar": { fr: "Maturité par axe", en: "Maturity by axis" },
  "results.staircase": { fr: "L'échelle Ariovis", en: "The Ariovis scale" },
  "results.strengths": { fr: "Vos forces", en: "Your strengths" },
  "results.gaps": { fr: "Écarts prioritaires", en: "Priority gaps" },
  "results.roadmap": { fr: "Roadmap en cinq étapes", en: "Five-step roadmap" },
  "results.priority_matrix": { fr: "Matrice de priorisation", en: "Priority matrix" },
  "results.offers": { fr: "Proposition d'accompagnement Ariovis", en: "Ariovis engagement proposal" },
  "results.ctas": { fr: "Prochaines actions", en: "Next actions" },
  "results.recommended": { fr: "Recommandé", en: "Recommended" },
  "results.constat": { fr: "Constat", en: "Finding" },
  "results.consequence": { fr: "Conséquence potentielle", en: "Potential consequence" },
  "results.capacity": { fr: "Capacité cible", en: "Target capability" },
  "results.urgency": { fr: "Urgence", en: "Urgency" },
  "results.dependency": { fr: "Dépendance", en: "Dependency" },
  "results.action_reco": { fr: "Action recommandée", en: "Recommended action" },
  "results.objective": { fr: "Objectif", en: "Objective" },
  "results.phase": { fr: "Phase", en: "Phase" },
  "results.horizon": { fr: "Horizon", en: "Horizon" },
  "results.target_level_short": { fr: "Niveau visé", en: "Target level" },
  "results.actions_key": { fr: "Actions clés", en: "Key actions" },
  "results.prerequisites": { fr: "Prérequis", en: "Prerequisites" },
  "results.dependencies": { fr: "Dépendances", en: "Dependencies" },
  "results.deliverables": { fr: "Livrables", en: "Deliverables" },
  "results.risks": { fr: "Risques", en: "Risks" },
  "results.quick_wins": { fr: "Quick wins", en: "Quick wins" },
  "results.foundations": { fr: "Fondations", en: "Foundations" },
  "results.structuring": { fr: "Chantiers structurants", en: "Structural workstreams" },
  "results.future": { fr: "Optimisations futures", en: "Future optimizations" },
  "results.offer_when": { fr: "Quand est-elle pertinente ?", en: "When is it relevant?" },
  "results.offer_contents": { fr: "Contenus", en: "Contents" },
  "results.offer_price": { fr: "Tarif", en: "Pricing" },
  "results.disclaimer": {
    fr: "Cette étude constitue une première orientation personnalisée à partir de vos réponses. Elle ne remplace pas un audit approfondi de votre architecture, de vos processus et de vos configurations.",
    en: "This study is a first personalized orientation based on your answers. It does not replace an in-depth audit of your architecture, processes and configurations.",
  },
  "results.not_evaluated": { fr: "Non évalué", en: "Not evaluated" },
  "results.level_current_badge": { fr: "ACTUEL", en: "CURRENT" },
  "results.level_target_badge": { fr: "CIBLE", en: "TARGET" },

  // Wizard done — success
  "success.title": { fr: "Merci — votre étude a été générée", en: "Thanks — your study was generated" },
  "success.sent": { fr: "Un email récapitulatif vient d'être envoyé à l'adresse indiquée avec le PDF en pièce jointe.", en: "A summary email has been sent to the address you provided with the PDF attached." },
};

/* ------------------------------------------------------------------ */
/* Translator helpers                                                 */
/* ------------------------------------------------------------------ */
export function t(key, lang, params) {
  const entry = dict[key];
  if (!entry) return key;
  let s = entry[lang] || entry[DEFAULT_LANG] || key;
  if (params) {
    Object.keys(params).forEach((k) => {
      s = s.replaceAll(`{${k}}`, String(params[k]));
    });
  }
  return s;
}

/* Bilingual object helper: given {fr, en}, return the right one */
export function pick(obj, lang) {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj[DEFAULT_LANG] || "";
}
